import { FetchParams, Middleware, RequestContext } from '@/api/generated'
import { Injector } from '@/config/dependency-injection'
import { DevelopmentService } from '@/shared/development/development-service'
import { Maybe } from 'purify-ts'
import { HttpResponse } from '@/commons/types/http-response'
import { parseJson } from '@/shared/utils/json'
import { randomString } from '@/shared/utils/random'

interface UrlDefinition {
  content?: string
  method: string
  url: RegExp
}

export class KillUrlsMiddleware implements Middleware {
  // This regex means: "[maybe hash] [method] [url as regex] [json object that is used as response]
  private URL_DEFINITION_REGEX = /(#[ ]*)?(GET|PUT|POST|DELETE|OPTIONS)\s(.*?)(?:\s(?:({.*})))?$/

  private developmentService = Injector.get(DevelopmentService)

  public pre(context: RequestContext): Promise<FetchParams | void> {
    return new Promise<FetchParams | void>((resolve, reject) => {
      const urls = this.developmentService.getSettings().killUrls

      const urlDefinitions = urls.map(url => this.parseUrlDefinition(url))

      Maybe.fromNullable(urlDefinitions.find(url => this.checkUrl(url, context)))
        .map(urlDefinition => {
          const answer: HttpResponse<unknown> = parseJson<HttpResponse>(
            urlDefinition.content
          ).orDefaultLazy(() => ({
            status: 500,
            data: undefined,
            headers: new Map<string, string>([['x-response-id', `@${randomString()}`]]),
            statusText: 'INTERNAL SERVER ERROR',
          }))

          reject(answer)
        })
        .ifNothing(() => {
          resolve(context)
        })
    })
  }

  private checkUrl(urlDefinition: UrlDefinition | undefined, context: RequestContext): boolean {
    return (
      urlDefinition !== undefined &&
      urlDefinition.method === context.init.method &&
      urlDefinition.url.test(context.url)
    )
  }

  private parseUrlDefinition(urlDefinition: string): UrlDefinition | undefined {
    let parsedUrl: UrlDefinition | undefined

    const match = this.URL_DEFINITION_REGEX.exec(urlDefinition)

    if (match !== null && match.length > 2) {
      const [, ignore, method, url, content] = match

      if (ignore === undefined) {
        parsedUrl = {
          content,
          method,
          url: new RegExp(url),
        }
      }
    }

    return parsedUrl
  }
}
